import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { fmtDateTime, PageTitle } from '../ui';
import { getEvents, postEventsRsvp } from '@/api/parent';
import { Banner, Empty, ErrorBox, Loading, errMsg, unwrap } from '../ui';

interface EventRow { id: number; title: string; description: string | null; type: string; starts_at: string; ends_at: string | null; all_day: boolean; location: string | null; rsvp: 'yes' | 'no' | 'maybe' | null; rsvp_open: boolean }

const monthRange = (m: string) => {
  const [y, mo] = m.split('-').map(Number);
  const pad = (n: number) => String(n).padStart(2, '0');
  return { from: `${y}-${pad(mo)}-01`, to: `${y}-${pad(mo)}-${pad(new Date(y, mo, 0).getDate())}` };
};

export function CalendarPage() {
  const qc = useQueryClient();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [err, setErr] = useState('');
  const range = monthRange(month);
  const q = useQuery({ queryKey: ['parent-events', month], queryFn: () => unwrap<EventRow[]>(getEvents(range)) });
  const rsvp = useMutation({
    mutationFn: (v: { id: number; response: 'yes' | 'no' | 'maybe' }) => postEventsRsvp(v.id, { response: v.response }),
    onSuccess: () => { setErr(''); qc.invalidateQueries({ queryKey: ['parent-events'] }); },
    onError: (e) => setErr(errMsg(e)),
  });
  const shift = (d: number) => { const [y, m] = month.split('-').map(Number); const dt = new Date(y, m - 1 + d, 1); setMonth(`${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`); };

  return (
    <div className="space-y-5">
      <PageTitle kicker="School" title="Calendar & events" sub="School events, holidays and meetings. Tell the school if you are coming." />
      {err && <Banner tone="error" onClose={() => setErr('')}>{err}</Banner>}
      <Card title={new Date(`${month}-01`).toLocaleDateString('en-NG', { month: 'long', year: 'numeric' })} action={<div className="flex gap-1"><Button variant="ghost" onClick={() => shift(-1)}>‹</Button><Button variant="ghost" onClick={() => shift(1)}>›</Button></div>}>
        {q.isLoading ? <Loading /> : q.isError ? <ErrorBox error={q.error} onRetry={() => q.refetch()} /> : !q.data?.length ? <Empty text="No events this month." /> : (
          <ul className="divide-y">
            {q.data.map((e) => (
              <li key={e.id} className="py-3 flex flex-col sm:flex-row sm:justify-between gap-2 sm:gap-4">
                <div>
                  <div className="font-medium">{e.title} <span className="text-xs text-slate-500">({e.type})</span></div>
                  <div className="text-sm text-slate-500">{e.all_day ? new Date(e.starts_at).toLocaleDateString('en-NG') : fmtDateTime(e.starts_at)}{e.location ? ` · ${e.location}` : ''}</div>
                  {e.description && <div className="text-sm mt-1">{e.description}</div>}
                </div>
                {e.rsvp_open && (
                  <div className="flex gap-1 items-start shrink-0">
                    {(['yes', 'maybe', 'no'] as const).map((r) => <Button key={r} variant={e.rsvp === r ? 'primary' : 'ghost'} disabled={rsvp.isPending} onClick={() => rsvp.mutate({ id: e.id, response: r })}>{r === 'yes' ? 'Going' : r === 'no' ? "Can't go" : 'Maybe'}</Button>)}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
